/**
 * useAuth Hook
 * Manages authentication state with httpOnly cookie-based sessions.
 *
 * Tokens are stored in httpOnly cookies set by the server.
 * The frontend no longer reads/writes tokens to localStorage.
 * On mount, calls /api/auth/me to check for an existing cookie session.
 * Periodic refresh keeps the session alive via /api/auth/refresh-token.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { api } from '../api/client';

// Check session refresh every 6 hours
const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * Authentication hook
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // true on mount to check session
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);

  // Check session on mount - server validates cookie
  useEffect(() => {
    let cancelled = false;

    api.getCurrentUser()
      .then(data => {
        if (!cancelled) setUser(data.user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Schedule periodic refresh to keep cookie alive
  useEffect(() => {
    if (!user) return;

    const refreshSession = async () => {
      try {
        await api.refreshTokenWithRotation();
      } catch {
        // If refresh fails, check if still authenticated
        try {
          const data = await api.getCurrentUser();
          setUser(data.user);
        } catch {
          setUser(null);
        }
      }
    };

    refreshIntervalRef.current = setInterval(refreshSession, REFRESH_INTERVAL_MS);
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [user]);

  /**
   * Register a new account
   */
  const register = useCallback(async (email, password, businessName) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.register({ email, password, businessName });
      setUser(data.organizer);
      return data.organizer;
    } catch (err) {
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.login({ email, password });
      setUser(data.organizer);
      return data.organizer;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout - clear state and tell server to revoke cookies
   */
  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Ignore logout errors - clear state anyway
    }

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    setUser(null);
    setError(null);
  }, []);

  /**
   * Request magic link for passwordless login
   */
  const requestMagicLink = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    try {
      await api.requestMagicLink({ email });
      return true;
    } catch (err) {
      setError(err.message || 'Failed to send magic link');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify magic link token
   */
  const verifyMagicLink = useCallback(async (magicToken) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.verifyMagicLink({ token: magicToken });
      setUser(data.organizer);
      return data.organizer;
    } catch (err) {
      setError(err.message || 'Invalid or expired magic link');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request password reset
   */
  const requestPasswordReset = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    try {
      await api.requestPasswordReset({ email });
      return true;
    } catch (err) {
      setError(err.message || 'Failed to send reset link');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset password with token
   */
  const resetPassword = useCallback(async (resetToken, newPassword) => {
    setLoading(true);
    setError(null);
    try {
      await api.resetPassword({ token: resetToken, password: newPassword });
      return true;
    } catch (err) {
      setError(err.message || 'Failed to reset password');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Change password (when logged in)
   */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);
    try {
      await api.changePassword({ currentPassword, newPassword });
      await logout();
      return true;
    } catch (err) {
      setError(err.message || 'Failed to change password');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [logout]);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async () => {
    if (!user?.id) return null;
    try {
      const data = await api.getOrganizer(user.id);
      setUser(data);
      return data;
    } catch (err) {
      if (err.status === 401) {
        setUser(null);
      }
      return null;
    }
  }, [user?.id]);

  /**
   * Update user data locally (after successful API update)
   */
  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Computed properties
  const isAuthenticated = useMemo(() => !!user, [user]);
  const isOnboardingComplete = useMemo(() => !!user?.onboardingComplete, [user]);
  const canReceivePayments = useMemo(
    () => !!user?.chargesEnabled && !!user?.detailsSubmitted,
    [user]
  );

  return {
    // State
    user,
    token: null, // Deprecated - tokens are now in httpOnly cookies
    loading,
    error,

    // Computed
    isAuthenticated,
    isOnboardingComplete,
    canReceivePayments,

    // Actions
    register,
    login,
    logout,
    requestMagicLink,
    verifyMagicLink,
    requestPasswordReset,
    resetPassword,
    changePassword,
    refreshUser,
    updateUser,
    clearError,

    // For manual control (use sparingly)
    setUser,
  };
}

export default useAuth;
